// ─── SIGNAL CONVERGENCE BOARD ─────────────────────────────────────────────────
// Aggregated, cache-first regime dashboard. Reads the lightweight backend
// summary endpoints (all Redis-only, never 502) plus the shared globalSignals
// bus, and renders a compact convergence view. This build ships the skeleton +
// auto-refresh + Sections 1 (regime summary) and 2 (compact sentiment). Domain
// cards (Section 3) and the synthesis row (Section 4) land in the next phase.
//
// Signal DETECTION is NOT done here — it runs in SignalBoard, which App keeps
// mounted (hidden) alongside this component so globalSignals.activeSignals /
// portfolioExposures stay populated. This component only READS globalSignals.

import { useState, useEffect, useMemo } from "react";
import { C, s, backendFetch, AnalystButton } from "./core";
// ⚠ "./components/Chyron", not "../components/Chyron" — this file lives at
// src/ConvergenceBoard.jsx, so ../ would resolve outside src/ entirely.
import { Chyron } from "./components/Chyron";
import { LiveTape } from "./components/LiveTape";
// ⚠ The independent-position read is NOT reimplemented here. It is the same
// component the Watchlist tab renders, over the same object FactorExposurePanel
// already publishes to the bus — so the two surfaces cannot drift into showing
// different groupings of the same holdings. No cycle: FactorExposurePanel imports
// core / factorEngine / frenchFactors and nothing from this file.
import { IndependentPositions } from "./FactorExposurePanel";

const REFRESH_MS = 5 * 60 * 1000; // 5 minutes
// Chars of digest shown before "READ MORE". ⚠⚠ 280 WAS A STUB, NOT A PREVIEW.
// The digest arrives WHOLE — measured 2026-09-22 at 1,230 chars, ending on real
// terminal punctuation — so 280 was hiding 77% of a complete thought behind a 9px
// toggle, which reads as the backend having truncated. 700 carries the regime call
// and the reason it holds (the first three or four sentences) and leaves a real
// remainder, so the toggle still means something.
// ⚠ DIGEST_COLLAPSED_H BELOW MUST CLEAR THIS. The slice is the truncation; the
// max-height only carries the transition. If the box binds first the text is cut
// with no ellipsis and no affordance — worse than the stub this replaces.
const DIGEST_MAX = 700;
// Digest box heights in px, for the growth transition only.
// ⚠ DERIVED, NOT GUESSED. The block inherits 12px JetBrains Mono — monospace, so
// ~7.2px per char — at lineHeight 1.7, i.e. 20.4px per line. At the narrowest
// viewport this app renders (~320px ≈ 38 chars/line) DIGEST_MAX wraps to ~19
// lines, call it 21 for the ragged right edge of prose: ≈430px. 560 clears that
// at every width. The previous 360 did NOT, which is why this is a named constant
// now rather than a literal that drifts away from the slice it has to clear.
const DIGEST_COLLAPSED_H = 560;
// ⚠⚠ COUPLED TO THE BACKEND DIGEST CAP — re-derive if that moves. _haiku_digest
// caps at 400 tokens ≈ 1,600 chars; at 38 chars/line that is ~42 lines ≈ 860px,
// ≈960px with wrap waste, so 1200 clears a maximum-length digest even on the
// narrowest screen. At a 700-token backend cap the same digest would need
// ~1,510px and would clip here silently, with no ellipsis and no scrollbar.
const DIGEST_EXPANDED_H = 1200;

// 0 fear (red) → 100 greed (green). Mirrors the QS gauge coloring on the
// Sentiment page (QSFearGreedPanel), kept local to avoid a cross-file import.
const qsColor = (v) => v == null ? C.textDim
  : v <= 20 ? C.red : v <= 40 ? C.orange : v <= 60 ? C.amber : v <= 80 ? C.teal : C.green;

// Compact QS Fear/Greed card for the Convergence Board's sentiment section:
// composite + label + the three most-populated component bars (VIX, Breadth,
// SPY) + an X/5 signals indicator. Self-contained fetch of /api/sentiment/qsfg.
// ⚠ THE FETCH WAS LIFTED TO THE PARENT (2026-08-15) and this is now a pure
// presentational component. The FLOW card needs the same payload for its HY-spread
// line, and a second /api/sentiment/qsfg call from there would have doubled the
// request AND let the two cards disagree — the endpoint recomputes on a cache miss,
// so two calls seconds apart can legitimately return different
// components_available. One fetch, one object, both cards.
function QSFearGreedCompact({ qsfg, status }) {
  const composite = qsfg && typeof qsfg.composite === "number" ? qsfg.composite : null;
  const comps = (qsfg && qsfg.components) || {};
  const available = qsfg?.data_quality?.components_available ?? 0;
  const col = qsColor(composite);
  // ⚠ Formatted here, not by the parent: the same payload feeds the FLOW card,
  // which has no use for a timestamp. Mirrors the full panel's formatting in
  // CryptoSentiment.jsx so the two never disagree about what time it is.
  // ⚠ Guarded — an unparseable computed_at must render nothing rather than
  // "Invalid Date", which reads as a broken feed.
  const asOf = qsfg?.computed_at
    ? (() => {
        try {
          return new Date(qsfg.computed_at).toLocaleTimeString("en-US", {
            timeZone: "America/New_York", hour: "2-digit", minute: "2-digit", hour12: false,
          });
        } catch { return null; }
      })()
    : null;
  // The composite is still building its EWMA history — every component fell back
  // to the absolute lerp bounds. ⚠ Strict equality on "lerp": "mixed" means most
  // components ARE z-scored and does not warrant the caveat.
  const calibrating = qsfg?.composite_method === "lerp";
  const BARS = [
    { key: "vix",          name: "VIX" },
    { key: "breadth",      name: "Breadth" },
    { key: "spy_momentum", name: "SPY" },
  ];

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: C.textDim, letterSpacing: "0.1em" }}>QS FEAR / GREED</span>
        <span style={{ fontSize: 9, color: C.textDim }}>{available}/5 signals</span>
      </div>
      {composite == null ? (
        <div style={{ fontSize: 11, color: C.textDim, padding: "8px 0", lineHeight: 1.5 }}>
          {status === "loading" ? "Loading fear/greed…" : `Fear/Greed unavailable — awaiting market data. ${available}/5 signals active.`}
        </div>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 10 }}>
            <span style={{ fontSize: 34, fontWeight: 700, lineHeight: 1, color: col }}>{Math.round(composite)}</span>
            {/* ⚠⚠ THE LABEL GOES AMBER ON ANY MISSING COMPONENT. The composite is
                a WEIGHTED MEAN renormalized over whatever resolved, so a component
                dropping out does not merely lower confidence — it MOVES THE
                HEADLINE NUMBER, and a 4/5 reading renders identically to a 5/5 one.
                The "N/5 signals" counter was already present but is 9px dim grey
                beside a 34px coloured number, so it lost. Colouring the LABEL (not
                the number) marks the reading as partial without discarding the data
                or implying a direction the score does not support.
                ⚠ Threshold was `< 3` and is now `< 5`: at `< 3` the live 3/5 state
                this platform sits in outside market hours showed no tell at all. */}
            <span style={{
              fontSize: 12, fontWeight: 700, letterSpacing: "0.05em",
              color: available < 5 ? C.amber : col,
            }}>{qsfg?.label ?? "—"}</span>
            {/* Scores are absolute lerp bounds until the EWMA history reaches
                MIN_ZSCORE_OBS. Says so rather than presenting a calibrating
                reading as a settled one. Disappears by itself. */}
            {calibrating && (
              <span style={{ fontSize: 9, color: C.textDim }}>(calibrating)</span>
            )}
          </div>
          {asOf && (
            <div style={{ fontSize: 9, color: C.textDim, marginTop: -6, marginBottom: 8 }}>
              as of {asOf} ET
            </div>
          )}
          <div style={{ display: "grid", gap: 5 }}>
            {BARS.map(({ key, name }) => {
              const c = comps[key];
              const sc = c && typeof c.score === "number" ? c.score : null;
              return (
                <div key={key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 9, color: C.textDim, width: 54 }}>{name}</span>
                  <span style={{ flex: 1, height: 6, background: C.border, borderRadius: 3, overflow: "hidden" }}>
                    {/* ⚠ FLOOR OF 2, NOT 0 — a component scoring exactly 0 has REAL
                        data. Put/call reaches it routinely (the service's _lerp
                        clamps at a ratio of 1.2 and SPY printed 1.215), and at a 0
                        floor the fill was zero-width, i.e. indistinguishable from the
                        empty track, while the number beside it read 0. Safe to floor
                        unconditionally here because the whole span is already gated on
                        `sc != null`, so an ABSENT component renders no fill at all —
                        unlike the full panel in CryptoSentiment.jsx, where the same
                        fix had to sit inside its `has ?` branch. */}
                    {sc != null && (
                      <span style={{ display: "block", width: `${Math.max(2, Math.min(100, sc))}%`, height: "100%", background: qsColor(sc) }} />
                    )}
                  </span>
                  <span style={{ fontSize: 9, width: 26, textAlign: "right", color: sc != null ? C.text : C.textDim }}>
                    {sc != null ? Math.round(sc) : "—"}
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// Backend-only summary endpoints (built earlier — Redis-only, never 502).
const SUMMARY_PATHS = [
  "/api/digest/market",
  "/api/dashboard/rates",
  "/api/dashboard/physical",
  "/api/dashboard/altdata",
];

// ─── SYNTHESIS INPUTS ─────────────────────────────────────────────────────────
// ⚠⚠ FETCHED FOR THE SYNTHESIS, NOT FOR THE RENDER. Everything else this board
// loads is drawn on screen; these four are loaded so the FULL SYNTHESIS has them.
// Before 2026-08-25 the board sent the backend a single sentence — "Cross-asset
// signal convergence board synthesis" — and the backend tried to re-fetch every
// domain from Redis, where four of five silently resolved to nothing while these
// same numbers sat in the browser. The board now forwards what it holds.
// ⚠ /api/sentiment/bullbear is CONTRARIAN and INVERTED relative to qsfg: 10 is
// crowded long and bearish. That polarity is stated explicitly in the context
// object (see buildSynthesisContext) — a consumer that infers it gets it backwards.
const BULLBEAR_PATH = "/api/sentiment/bullbear";
const STRESS_PATH   = "/api/sentiment/stress";
const UO_RECENT_PATH = "/api/stream/unusual/recent?limit=10";
const AIRCRAFT_EVENTS_PATH = "/api/altdata/aircraft/events";

// Top-N caps on what travels to the model. The synthesis is capped at four
// sentences; sending fifty dark-pool rows buys nothing and costs tokens.
const SYNTH_TOP_N = 5;

const num = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);

// ─── SIGNAL FIRST-SEEN ────────────────────────────────────────────────────────
// ⚠⚠ THIS IS A FRONTEND-COMPUTED FIRST-SEEN TIME, NOT A BACKEND EVENT TIME, AND
// THE LABEL SAYS SO. Two different clocks exist in this platform and they must not
// be confused:
//   · convergence_detector writes `triggered_at` on a real backend event — a
//     server-side fire time with server-side precision. That is NOT what this is.
//   · SignalBoard's rules fire CLIENT-side, and the only record of when is a map
//     it keeps at localStorage["qs_signal_first_seen"] — the moment THIS BROWSER
//     first saw the rule active. Clear site data and it resets; open the board on
//     a second device and it will disagree.
// So the UI says "Detected", never "fired at", and never implies server precision.
//
// ⚠⚠ THE KEY IS OWNED BY SignalBoard.jsx, WHICH IS THE SOLE WRITER — this file is
// a READ-ONLY consumer. That asymmetry is what makes duplicating the key name
// tolerable where the qs_position_sizes duplication (two writers) is a standing
// hazard: a reader cannot drift the data, only misread a rename. If SignalBoard
// ever renames it, this silently shows nothing rather than showing something wrong.
const SIGNAL_FIRST_SEEN_KEY = "qs_signal_first_seen";

function loadFirstSeen() {
  try {
    const raw = JSON.parse(localStorage.getItem(SIGNAL_FIRST_SEEN_KEY));
    return raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
  } catch { return {}; }
}

// "Detected 12m ago" under an hour, "Detected 14:32 ET" beyond it, nothing when
// the id was never stamped.
// ⚠ Relative under the hour because that is the window where "how long has this
// been firing" is the live question; absolute beyond it because "3h ago" is
// harder to place against the session than a clock time.
// ⚠ Number-checked before arithmetic: a corrupt entry (null, "", []) all coerce
// through Number() to 0, which would render as a 1970 timestamp — the same trap
// the position-size reader in this file already guards.
function detectedLabel(firstSeen, id) {
  if (!id) return null;
  const raw = firstSeen ? firstSeen[id] : null;
  if (typeof raw !== "number" || !Number.isFinite(raw) || raw <= 0) return null;
  const ageMs = Date.now() - raw;
  if (ageMs < 0) return null;                      // clock skew — say nothing
  if (ageMs < 60 * 60 * 1000) return `Detected ${Math.max(0, Math.floor(ageMs / 60000))}m ago`;
  try {
    const t = new Date(raw).toLocaleTimeString("en-US", {
      timeZone: "America/New_York", hour12: false, hour: "2-digit", minute: "2-digit",
    });
    return `Detected ${t} ET`;
  } catch { return null; }
}

// Signal ids suppressed from the ACTIVE SIGNALS card while their data source is
// unwired. See the filter in Card 1 for the full reasoning and how to restore.
const _HIDDEN_SIGNAL_IDS = new Set(["tanker-dark-cluster"]);

// ─── POSITION SIZING (read-only mirror of the Watchlist tab's calculator) ──────
// ⚠⚠ BOTH KEYS ARE OWNED BY WatchlistTab.jsx, which writes them. This file only
// READS. Nothing enforces that the two copies stay in step — if a name changes
// there, it must change here too.
const POSITION_SIZES_KEY = "qs_position_sizes";     // { TICKER: shares }
const CALC_BUDGET_KEY    = "qs_calculator_budget";  // string, as typed

// ⚠⚠ TYPE-CHECK BEFORE Number(), mirroring WatchlistTab's reader. null, "", false
// and [] ALL coerce to 0, so a corrupt entry would otherwise survive as a
// legitimate "0 shares" and silently undercount the allocation. This exact defect
// was caught by the calculator's harness; do not simplify it back to Number(v).
const readPositionSizes = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(POSITION_SIZES_KEY));
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
    const out = {};
    Object.entries(raw).forEach(([sym, v]) => {
      const numeric = typeof v === "number"
        || (typeof v === "string" && v.trim() !== "" && !Number.isNaN(Number(v)));
      if (!numeric) return;
      const n = Math.floor(Number(v));
      if (Number.isFinite(n) && n >= 0) out[String(sym).toUpperCase()] = n;
    });
    return out;
  } catch {
    return {};
  }
};

// null when unset or unparseable — the Remaining line is omitted rather than
// rendered against a guessed budget.
const readCalcBudget = () => {
  try {
    const raw = localStorage.getItem(CALC_BUDGET_KEY);
    if (!raw) return null;
    const n = Number.parseFloat(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
};

// Compact notional for the dark-pool card. ⚠ The K→M→B promotion tests the ROUNDED
// value, not the raw one — the same boundary bug the backend's fmt_money carried,
// where $999,999 rendered "$1000K".
const fmtNotional = (v) => {
  const n = num(v);
  if (n == null) return "—";
  const a = Math.abs(n);
  if (a >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (a >= 1e6) {
    const m = n / 1e6;
    return Math.round(m) >= 1000 ? `$${(n / 1e9).toFixed(1)}B` : `$${m.toFixed(1)}M`;
  }
  if (a >= 1e3) {
    const k = n / 1e3;
    return Math.round(k) >= 1000 ? `$${(n / 1e6).toFixed(1)}M` : `$${Math.round(k)}K`;
  }
  return `$${Math.round(n)}`;
};

async function fetchJSON(path) {
  // backendFetch(directUrl, backendPath): in prod the backend path is used with
  // the auth token; the first arg is only the local-dev direct fallback.
  const res = await backendFetch(path, path);
  if (!res.ok) throw new Error(String(res.status));
  return res.json();
}

// ─── RELEVANCE FRAMEWORK (duplicated from SignalBoard.jsx — kept independent) ──
// Byte-for-byte copies of the constants + functions in SignalBoard.jsx.
// Duplicated (not imported) so the two boards stay decoupled — keep these in
// sync if SignalBoard's copies change. Factor names match
// globalSignals.portfolioExposures keys exactly (beta, momentum, volatility,
// value, quality, size). Display-only; no detection happens here.
const SIGNAL_FACTOR_MAP = {
  "vix-skew-divergence": {
    factors: ["beta", "volatility"],
    rationale: "Volatility-regime divergence raises beta and volatility factor sensitivity as the VIX term structure flattens toward inversion.",
  },
  "vix-fear": {
    factors: ["beta", "volatility"],
    rationale: "Elevated fear widens dispersion, amplifying beta and volatility factor exposure.",
  },
  "yield-curve-inversion": {
    factors: ["value", "quality"],
    rationale: "Late-cycle risk-off rotation favors value and quality over high-beta growth.",
  },
  "monetary-regime": {
    factors: ["value", "momentum"],
    rationale: "Inflationary money-growth regime rewards value and sustains momentum in hard assets.",
  },
  "fiscal-dominance": {
    factors: ["value", "momentum"],
    rationale: "Constrained Fed policy pressures duration, tilting returns toward value and momentum.",
  },
  "dark-pool-defended": {
    factors: ["beta"],
    rationale: "Institutional level defense acts on the underlying's beta sensitivity most directly.",
  },
  "squeeze": {
    factors: ["momentum", "volatility"],
    rationale: "Forced covering drives momentum alongside a sharp volatility expansion.",
  },
  "pc-put-heavy": {
    factors: ["momentum", "volatility"],
    rationale: "Heavy put flow reflects directional and volatility positioning, loading on momentum and volatility.",
  },
  "ais-dark": {
    factors: ["momentum"],
    rationale: "Supply-disruption flow propagates through momentum in affected commodity names.",
  },
  "tanker-dark-cluster": {
    factors: ["momentum"],
    rationale: "Clustered dark vessels signal supply momentum across energy and shipping names.",
  },
  "petroleum-draw": {
    factors: ["momentum", "value"],
    rationale: "Sustained draws tighten supply, feeding energy momentum and value.",
  },
  "thermal-industrial": {
    factors: ["momentum"],
    rationale: "Operational-disruption heat signatures translate into sector momentum moves.",
  },
  "congress-trade": {
    factors: ["quality"],
    rationale: "Informed insider concentration correlates with quality-factor names.",
  },
  "sentiment-extreme": {
    factors: ["momentum", "beta"],
    rationale: "Contrarian reversals at sentiment extremes shift momentum and beta sensitivity.",
  },
};

const SIGNAL_ID_TO_TYPE = {
  "vix-skew-div":         "vix-skew-divergence",
  "yield-inverted":       "yield-curve-inversion",
  "yield-flat":           "yield-curve-inversion",
  "mv-gap-inflationary":  "monetary-regime",
  "fiscal-dominance":     "fiscal-dominance",
  "unusual-options-flow": "unusual-options",
  "congress-buy-cluster": "congress-trade",
  "tanker-dark-cluster":  "ais-dark",
  "petroleum-move":       "petroleum-draw",
  "news-negative":        "sentiment-extreme",
  "news-positive":        "sentiment-extreme",
  "sentiment-extreme":    "sentiment-extreme",
};

// Score → band label (thresholds per spec; note score 20 lands in LOW).
function relevanceBand(score) {
  if (score >= 80) return "CRITICAL";
  if (score >= 60) return "HIGH";
  if (score >= 40) return "MODERATE";
  if (score >= 20) return "LOW";
  return "MONITOR";
}

// 0-100 overlap score. Averages the user's exposure magnitude across the factors
// this signal loads on — each mapped factor weighted equally. No mapping / no
// usable exposure → 20.
function computeRelevanceScore(signal, exposures) {
  const type = signal && SIGNAL_ID_TO_TYPE[signal.id];
  const mapping = type ? SIGNAL_FACTOR_MAP[type] : null;
  if (!mapping || !mapping.factors || !mapping.factors.length) return 20;
  if (!exposures) return 20;
  const vals = [];
  for (const f of mapping.factors) {
    const v = exposures[f];
    if (v == null) continue;
    vals.push(Math.min(1, Math.abs(v))); // exposure treated on a 0-1 magnitude scale
  }
  if (!vals.length) return 20;
  const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
  return Math.round(Math.max(0, Math.min(100, avg * 100)));
}

// Short object-noun describing the signal's regime, keyed by canonical type.
const RELEVANCE_TYPE_PHRASE = {
  "vix-skew-divergence":   "volatility-regime moves",
  "vix-fear":              "volatility-regime moves",
  "yield-curve-inversion": "rate-environment shifts",
  "monetary-regime":       "monetary-regime shifts",
  "fiscal-dominance":      "fiscal-regime shifts",
  "dark-pool-defended":    "institutional-flow moves",
  "squeeze":               "squeeze dynamics",
  "pc-put-heavy":          "options-flow moves",
  "ais-dark":              "supply-disruption moves",
  "tanker-dark-cluster":   "supply-disruption moves",
  "petroleum-draw":        "energy-supply moves",
  "thermal-industrial":    "industrial-disruption moves",
  "congress-trade":        "insider-flow patterns",
  "sentiment-extreme":     "sentiment reversals",
};

// One plain-language sentence (<15 words) describing the factor relationship
// only — no recommendation, no advice.
function getRelevanceImplication(signal, exposures, score) {
  const type = signal && SIGNAL_ID_TO_TYPE[signal.id];
  const mapping = type ? SIGNAL_FACTOR_MAP[type] : null;
  if (!mapping || !mapping.factors || !mapping.factors.length || !exposures) {
    return "Limited factor overlap with the current watchlist basket.";
  }
  let topFactor = null;
  let topMag = -1;
  for (const f of mapping.factors) {
    const v = exposures[f];
    if (v == null) continue;
    const mag = Math.abs(v);
    if (mag > topMag) { topMag = mag; topFactor = f; }
  }
  if (!topFactor || topMag <= 0) {
    return "Limited factor overlap with the current watchlist basket.";
  }
  const phrase = RELEVANCE_TYPE_PHRASE[type] || "this signal";
  const level = topMag >= 0.66 ? "High" : topMag >= 0.33 ? "Moderate" : "Low";
  const verb  = topMag >= 0.66 ? "amplifies" : topMag >= 0.33 ? "aligns with" : "weakly aligns with";
  const factorName = topFactor.charAt(0).toUpperCase() + topFactor.slice(1);
  return `${level} ${factorName} exposure ${verb} ${phrase}.`;
}

// Band → badge palette (SignalBoard's RELEVANCE_BAND_STYLE). Existing C tokens +
// alpha only — no new color variables. dim=true renders MODERATE as muted amber.
const RELEVANCE_BAND_STYLE = {
  CRITICAL: { base: C.red,     dim: false },
  HIGH:     { base: C.orange,  dim: false },
  MODERATE: { base: C.orange,  dim: true  },
  LOW:      { base: C.textMid, dim: false },
  MONITOR:  { base: C.textDim, dim: false },
};

// Text color for a band label (matches SignalBoard's derivation).
function bandTextColor(band) {
  const st = RELEVANCE_BAND_STYLE[band] || RELEVANCE_BAND_STYLE.MONITOR;
  return st.base + (st.dim ? "AA" : "");
}

// ─── DOMAIN-CARD PRESENTATION HELPERS ─────────────────────────────────────────
// Relevance level → color (reuses existing tokens only; no new color values).
const RELEVANCE_COLOR = {
  HIGH:      C.red,
  WATCHLIST: C.red,
  MODERATE:  C.amber,
  FAVORABLE: C.green,
  LOW:       C.textDim,
};

// ── autocallable barrier badges ───────────────────────────────────────────────
// ⚠⚠ ATTACHED TO THE TICKER CONFLUENCE ROWS, NOT TO THE ACTIVE SIGNAL CARDS, AND
// THAT IS FORCED BY THE DATA. An activeSignals entry carries id / type / title /
// color / strength and NO SYMBOL — they are market-wide reads ("N vessels AIS
// dark", "congressional purchases across N tickers") whose tickers, where they
// exist at all, live in prose inside `desc`. There is no key to look a barrier up
// by. The dpConvergence rows carry h.symbol, so they are the only per-ticker card
// on this board and the only place this badge can be correct.
const BARRIER_ALERTS_PATH = "/api/altdata/autocallable/alerts";
// ⚠ SIX HOURS, tighter than the endpoint's 24. A badge is a claim about NOW; a
// breach that happened before lunch has already been priced and a still-glowing
// badge would misread as a live event. Stale alerts are not actionable.
const BARRIER_BADGE_MAX_AGE_MS = 6 * 3600 * 1000;

// {TICKER: severity} for HIGH/CRITICAL alerts inside the window. One fetch, one
// pass — never a request per row.
function indexBarrierAlerts(alerts) {
  const out = {};
  if (!Array.isArray(alerts)) return out;
  const cutoff = Date.now() - BARRIER_BADGE_MAX_AGE_MS;
  for (const a of alerts) {
    const sym = String(a?.ticker || "").trim().toUpperCase();
    const sev = String(a?.severity || "").toUpperCase();
    if (!sym || (sev !== "HIGH" && sev !== "CRITICAL")) continue;
    const t = Date.parse(a?.fired_at);
    // ⚠ An undateable alert is dropped rather than shown. The window is the whole
    // point; a row we cannot date is not evidence that it is current.
    if (!Number.isFinite(t) || t < cutoff) continue;
    // CRITICAL wins — a ticker that breached and then recovered inside the same
    // window should still read as the more serious of the two.
    if (out[sym] !== "CRITICAL") out[sym] = sev;
  }
  return out;
}

function BarrierBadge({ severity }) {
  if (severity !== "HIGH" && severity !== "CRITICAL") return null;
  const critical = severity === "CRITICAL";
  return (
    <span style={{
      fontSize: 8, fontWeight: critical ? 800 : 700, letterSpacing: "0.06em",
      color: critical ? C.red : C.amber,
      padding: "1px 5px", borderRadius: 2, whiteSpace: "nowrap", flexShrink: 0,
      background: (critical ? C.red : C.amber) + "18",
      border: `1px solid ${(critical ? C.red : C.amber)}44`,
      // ⚠ Only CRITICAL pulses. A breach is happening; "near" is a warning that can
      // sit still. Two pulsing severities would make neither of them mean anything.
      // ⚠ qsPulse is the platform-wide keyframe from index.html, already used by
      // this file at the trial banner and by four other tabs. A private keyframe
      // would need its own <style> injection and would drift from the rest.
      animation: critical ? "qsPulse 1.5s ease-in-out infinite" : undefined,
    }}>
      {critical ? "\ud83d\udea8 BARRIER BREACH" : "\u26a0 BARRIER NEAR"}
    </span>
  );
}

function Badge({ text, color }) {
  return (
    <span style={{
      fontSize: 8, fontWeight: 700, letterSpacing: "0.06em", color,
      padding: "1px 6px", background: color + "18",
      border: `1px solid ${color}33`, borderRadius: 2, whiteSpace: "nowrap",
    }}>
      {text}
    </span>
  );
}

function DataLine({ label, value, valueColor }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 10 }}>
      <span style={{ color: C.textDim, whiteSpace: "nowrap" }}>{label}</span>
      <span style={{
        color: valueColor || C.textMid, textAlign: "right",
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
      }}>
        {value}
      </span>
    </div>
  );
}

// Nav link uses the codebase's callback pattern (parent passes a setActiveTab-style
// fn). ConvergenceBoard isn't wired one today (App would need editing), so an
// absent onNavigate renders an inert pointer label rather than a dead click.
function NavLink({ tab, onNavigate }) {
  const clickable = typeof onNavigate === "function";
  return (
    <div
      onClick={clickable ? () => onNavigate(tab) : undefined}
      style={{
        marginTop: 8, fontSize: 9, color: C.amber, letterSpacing: "0.08em",
        cursor: clickable ? "pointer" : "default", userSelect: "none",
      }}
    >
      → {tab}
    </div>
  );
}

function DomainCard({ title, badge, relevance, navTab, onNavigate, span2, children }) {
  return (
    <div style={{
      ...s.card(), marginBottom: 0,
      gridColumn: span2 ? "span 2" : "auto",
      display: "flex", flexDirection: "column",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6, marginBottom: 6 }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", color: C.textMid }}>{title}</span>
        {badge && <Badge text={badge.text} color={badge.color} />}
      </div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 3 }}>{children}</div>
      {relevance && (
        <div style={{ fontSize: 9, color: RELEVANCE_COLOR[relevance.level] || C.textDim, marginTop: 6, lineHeight: 1.5 }}>
          ▸ {relevance.text}
        </div>
      )}
      {navTab && <NavLink tab={navTab} onNavigate={onNavigate} />}
    </div>
  );
}

// Amber trigger button used by the Dark Pool / Squeeze cards. 44px min height.
function TriggerButton({ label, onClick, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        alignSelf: "center", marginTop: 4, minHeight: 44, padding: "8px 16px",
        background: "transparent", border: `1px solid ${C.amber}`, color: C.amber,
        fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", borderRadius: 2,
        cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.6 : 1,
        fontFamily: "inherit",
      }}
    >
      {label}
    </button>
  );
}

// Nav link with explicit label text (NavLink renders the raw tab name; these
// cards want custom copy while still navigating to the real tab).
function CardNavLink({ label, tab, onNavigate, onClick }) {
  const handler = onClick || (typeof onNavigate === "function" ? () => onNavigate(tab) : undefined);
  const clickable = !!handler;
  return (
    <div
      onClick={handler}
      style={{
        marginTop: 8, fontSize: 9, color: C.amber, letterSpacing: "0.08em",
        cursor: clickable ? "pointer" : "default", userSelect: "none",
      }}
    >
      {label}
    </div>
  );
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────
export function ConvergenceBoard({ globalSignals = {}, onData, keys = {}, onNavigate, onDpNavigate, onTapeOpen }) {
  const [digest,      setDigest]      = useState(null);
  const [rates,       setRates]       = useState(null);
  const [physical,    setPhysical]    = useState(null);
  const [altdata,     setAltdata]     = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  // Market Regime expand/collapse — local only, always collapsed on mount
  // (navigating away and back resets it; nothing persisted).
  const [regimeOpen,  setRegimeOpen]  = useState(false);
  // Dark pool auto-populates from the platform beat cache; squeeze from the
  // candidates fetch below. Neither needs a button any more.
  const [dpScan,   setDpScan]   = useState(null);   // platform scan results (tiered)
  const [dpStatus, setDpStatus] = useState("loading"); // loading|sip|empty|error|done
  const [sqCands,  setSqCands]  = useState(null);   // local top-2 squeeze candidates
  const [sqStatus, setSqStatus] = useState("idle"); // idle|loading|computing|empty|error|done
  const [dpConvergence, setDpConvergence] = useState([]);   // dark-pool ∩ squeeze hits
  // {TICKER: 'HIGH'|'CRITICAL'} — barrier alerts inside the badge window.
  const [barrierAlerts, setBarrierAlerts] = useState({});
  const [sqCandMap,     setSqCandMap]     = useState(null);  // {SYM:{si_float,dtc}} cross-ref cache
  // QS Fear/Greed — lifted out of QSFearGreedCompact so the FLOW card can share it.
  const [qsfg,     setQsfg]     = useState(null);
  const [qsStatus, setQsStatus] = useState("offline");

  // ── Synthesis-only inputs. null means "not loaded / failed", and the backend
  // is told null explicitly rather than the domain being quietly omitted. ──
  const [bullBear, setBullBear] = useState(null);
  const [stress,   setStress]   = useState(null);
  const [uoRecent, setUoRecent] = useState(null);
  const [aircraftEvents, setAircraftEvents] = useState(null);
  // SPY put/call history — DB-backed (options_pc_history), newest row last.
  const [pcHist,   setPcHist]   = useState(null);
  // Position sizing summary — read-only mirror of the Watchlist calculator.
  const [posSizes,  setPosSizes]  = useState(readPositionSizes);
  const [posBudget, setPosBudget] = useState(readCalcBudget);
  const [posPrices, setPosPrices] = useState(null);   // { TICKER: price }

  // ── Auto-refresh: the 4 summary endpoints in parallel, on mount + every 5m ──
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const results = await Promise.allSettled(SUMMARY_PATHS.map(fetchJSON));
      if (cancelled) return;
      const [d, r, p, a] = results;
      if (d.status === "fulfilled") setDigest(d.value);
      if (r.status === "fulfilled") setRates(r.value);
      if (p.status === "fulfilled") setPhysical(p.value);
      if (a.status === "fulfilled") setAltdata(a.value);
      setLastUpdated(Date.now());
      setLoading(false);
    };

    load();
    const id = setInterval(load, REFRESH_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // ⚠ THE SIGNAL NEWS CARD AND ITS FETCH WERE REMOVED (2026-08-15), NOT HIDDEN.
  // /api/news/signals resolves through NewsService.fetch_gnews, which has been a
  // hard `return []` since the GNews key was revoked (backend d7bae3b) — GNews is
  // its ONLY source, so the endpoint returns {} for every input. Verified live:
  // three signal ids that DO match SIGNAL_NEWS_QUERIES still returned {}, and
  // news:signals:* held 0 cache keys. The card could not populate under any market
  // condition, so it rendered a permanent "no coverage" panel across two grid
  // columns. Restoring it needs a working GNEWS_API_KEY (or a Currents/RSS path)
  // AND a reconciliation of the id vocabularies — roughly half the board's rule ids
  // do not match the backend's keys (vix-skew-div vs vix-skew-divergence,
  // yield-inverted vs yield-curve-inversion, dark-pool-squeeze vs dark-pool-defended).
  // The `signalIds`/`signalKey` memo and the `truncate` helper went with it; they
  // had no other consumer.

  // ── Derived display values (all null-safe) ──
  const digestText = typeof digest?.digest === "string" ? digest.digest.trim() : "";
  const digestTruncated = digestText.length > DIGEST_MAX;
  // Truncation is a JS substring, so the collapsed render never holds the full
  // text — expanding swaps in digestText rather than revealing hidden overflow.
  const digestShown = digestTruncated && !regimeOpen
    ? digestText.slice(0, DIGEST_MAX).trimEnd() + "…"
    : digestText;

  const exposures = globalSignals?.portfolioExposures;
  const hasPortfolio = exposures && typeof exposures === "object" &&
    Object.values(exposures).some((v) => typeof v === "number" && Number.isFinite(v));
  // ── Barrier alerts, one fetch on mount ──
  // ⚠ ONE REQUEST FOR THE WHOLE BOARD, not one per row. The endpoint already
  // returns the full tape; indexing it by ticker client-side is free, while a
  // per-row fetch would issue a request for every confluence hit on every
  // render and most of them would 404 into nothing.
  // ⚠ Fails silently to {} — no badge is the correct rendering when the tape is
  // unavailable, and an error state here would put a failure banner on a board
  // whose actual content loaded fine.
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await backendFetch(null, BARRIER_ALERTS_PATH);
        if (!res?.ok) return;
        const data = await res.json();
        if (live) setBarrierAlerts(indexBarrierAlerts(data?.alerts));
      } catch { /* no badge is the correct rendering */ }
    })();
    return () => { live = false; };
  }, []);

  // The independent-position result published by FactorExposurePanel, which
  // App.jsx mounts hidden under this tab for exactly this reason.
  // ⚠ ABSENT UNTIL THAT PANEL RESOLVES AT LEAST TWO PRICEABLE SYMBOLS — it
  // early-returns below two watchlist symbols and again when fewer than two
  // price successfully, publishing nothing in either case. The YOUR WATCHLIST
  // slot degrades to its empty state until then, which is why that branch is
  // kept rather than relying on the component's own null return.
  // ⚠ This used to be read inside the Watchlist Context card's IIFE; that card
  // is gone and this is the only reader left.
  const pip = globalSignals?.portfolioIndependentPositions;

  const updatedLabel = lastUpdated ? new Date(lastUpdated).toLocaleTimeString() : "—";

  // Watchlist symbols (localStorage) — used by the darkpool/squeeze relevance.
  const watchlist = useMemo(() => {
    try {
      const raw = JSON.parse(localStorage.getItem("fterm_watchlist"));
      if (!Array.isArray(raw)) return [];
      return raw
        .map((x) => (typeof x === "string" ? x : x?.symbol))
        .filter(Boolean)
        .map((sym) => String(sym).toUpperCase());
    } catch {
      return [];
    }
  }, []);

  // ── Personal relevance per domain from portfolioExposures (null → omit line) ──
  const relevanceFor = (domain) => {
    if (!hasPortfolio) return null;
    const beta = num(exposures?.beta);
    const momentum = num(exposures?.momentum);
    const value = num(exposures?.value);
    const quality = num(exposures?.quality);
    const inList = (sym) => !!sym && watchlist.includes(String(sym).toUpperCase());

    switch (domain) {
      case "rates":
        if (value != null && value > 0.5) return { level: "FAVORABLE", text: "Your value tilt is favored in the current rate regime." };
        if (beta != null && beta > 1.2) return { level: "MODERATE", text: `High beta (${beta.toFixed(2)}) raises rate sensitivity.` };
        return { level: "LOW", text: "Limited direct rate sensitivity in your watchlist." };
      case "flow":
        if (beta != null && beta > 1.2) return { level: "HIGH", text: `High-beta basket (${beta.toFixed(2)}) is exposed to flow-driven moves.` };
        if (beta != null && beta >= 0.8) return { level: "MODERATE", text: `Market-like beta (${beta.toFixed(2)}) — average flow exposure.` };
        if (beta != null) return { level: "LOW", text: `Defensive beta (${beta.toFixed(2)}) dampens flow exposure.` };
        return { level: "LOW", text: "Beta unavailable — flow exposure unclear." };
      case "physical":
        if (momentum != null && momentum > 0.6) return { level: "HIGH", text: "High momentum tilt is exposed to supply-shock reversals." };
        if (momentum != null && momentum >= 0.3) return { level: "MODERATE", text: "Moderate momentum exposure to physical-world signals." };
        return { level: "LOW", text: "Limited exposure to physical-world supply signals." };
      case "darkpool": {
        const sym = globalSignals?.darkPoolScan?.topSymbol;
        if (inList(sym)) return { level: "WATCHLIST", text: `Watchlist name ${String(sym).toUpperCase()} is showing dark-pool activity.` };
        if (beta != null && beta > 1.2) return { level: "MODERATE", text: "High-beta basket is sensitive to institutional flow." };
        return { level: "LOW", text: "No watchlist names in current dark-pool prints." };
      }
      case "squeeze": {
        const sym = globalSignals?.squeezeConvergence?.symbol;
        if (inList(sym)) return { level: "WATCHLIST", text: `Watchlist name ${String(sym).toUpperCase()} shows squeeze convergence.` };
        if (momentum != null && momentum > 0.6) return { level: "MODERATE", text: "High momentum tilt aligns with squeeze dynamics." };
        return { level: "LOW", text: "No watchlist names in squeeze convergence." };
      }
      case "altdata":
        if (quality != null && quality > 0.6) return { level: "FAVORABLE", text: "Quality tilt is well-positioned for these flows." };
        return { level: "MODERATE", text: "Moderate exposure to government / alt-data flows." };
      default:
        return null;
    }
  };

  // Squeeze-candidate map (symbol → {si_float, dtc}) for the dark-pool ∩ squeeze
  // cross-reference — fetched once and cached; /api/squeeze/candidates is the only
  // source with DTC. Never blocks; empty map when unavailable.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const path = "/api/squeeze/candidates?limit=100&min_score=40";
        const res = await backendFetch(path, path);
        const d = await res.json();
        const cands = Array.isArray(d?.candidates) ? d.candidates : [];
        const map = {};
        for (const c of cands) {
          if (c && c.symbol) map[String(c.symbol).toUpperCase()] = { si_float: c.si_float, dtc: c.dtc };
        }
        if (cancelled) return;
        setSqCandMap(map);
        // ⚠ SAME RESPONSE, NO SECOND REQUEST. This fetch already existed purely to
        // build the cross-reference map, while the SQUEEZE card sat behind a LOAD
        // CANDIDATES button hitting the identical endpoint. The card now populates
        // from the data already in hand; the button survives only as the retry path
        // for the computing/empty/error states below.
        if (d && d.computing) { setSqStatus("computing"); return; }
        setSqCands(cands.length ? cands.slice(0, 2) : null);
        setSqStatus(cands.length ? "done" : "empty");
      } catch {
        if (!cancelled) { setSqCandMap({}); setSqStatus("error"); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── QS Fear/Greed — one fetch, shared by the sentiment card and FLOW ──
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      setQsStatus("loading");
      backendFetch("https://data.alpaca.markets", "/api/sentiment/qsfg")
        .then((r) => r.json())
        .then((d) => { if (!cancelled) { setQsfg(d); setQsStatus(d && d.composite != null ? "live" : "offline"); } })
        .catch(() => { if (!cancelled) setQsStatus("offline"); });
    };
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // ── Synthesis inputs: bull/bear, stress, unusual options, aircraft events ──
  // Same shape as the qsfg fetch above: one effect, cancelled on unmount, and a
  // failure sets null rather than surfacing an error. ⚠ NONE OF THESE IS AN ERROR
  // CONDITION FOR THE BOARD — they feed the synthesis only, so a miss must never
  // block a render. The synthesis is told which ones are null.
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      const grab = (path, setter, pick) =>
        backendFetch("https://data.alpaca.markets", path)
          .then((r) => r.json())
          .then((d) => { if (!cancelled) setter(pick ? pick(d) : d); })
          .catch(() => { if (!cancelled) setter(null); });

      grab(BULLBEAR_PATH, setBullBear,
        (d) => (d && d.score != null ? d : null));
      grab(STRESS_PATH, setStress,
        (d) => (d && d.level != null ? d : null));
      // ⚠ Response unwrapping: this endpoint returns {results, total_signals,
      // source, as_of}, not a bare array. Sending the wrapper would hand the
      // model an object it cannot read as a list of signals.
      grab(UO_RECENT_PATH, setUoRecent,
        (d) => (d && Array.isArray(d.results) && d.results.length ? d.results : null));
      // ⚠ Same again: {events, arrivals_count, departures_count, total}.
      grab(AIRCRAFT_EVENTS_PATH, setAircraftEvents,
        (d) => (d && Array.isArray(d.events) && d.events.length ? d.events : null));
    };
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // ── SPY put/call history ──
  // ⚠⚠ THE DB-BACKED ENDPOINT, NOT THE REDIS KEY. opts:hist:pc:SPY:90 is written
  // only by the QS warmer at a 1h TTL and was absent when this was built; more to
  // the point a browser cannot read Redis at all. /api/options/history/pc/{symbol}
  // serves the options_pc_history TABLE, so it survives a cold cache.
  // ⚠ The response is a BARE LIST sorted ASCENDING by date — the newest row is the
  // LAST element, not the first. Reading [0] would pin the card to a 90-day-old
  // ratio that still looks like a plausible reading.
  // ⚠ Structurally one trading day behind: options_pc_history is written by the
  // 16:30 ET snapshot beat, so there is never a row for the current session.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const path = "/api/options/history/pc/SPY?days=90";
        const res = await backendFetch(path, path);
        if (!res.ok) throw new Error(String(res.status));
        const data = await res.json();
        if (!cancelled) setPcHist(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setPcHist([]);
      }
    };
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // ── Position sizing summary ──
  // ⚠ localStorage is RE-READ on every tick, not just at mount. The Watchlist tab
  // owns these keys and storage is not reactive, so a size edited there would
  // otherwise stay invisible here until a full remount.
  // ⚠ Only tickers that are BOTH on the current watchlist AND sized above zero are
  // fetched — orphaned sizes from before remove() started clearing them must not
  // inflate the count, and a 0-share entry is not a position.
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const sizes = readPositionSizes();
      const budget = readCalcBudget();
      if (cancelled) return;
      setPosSizes(sizes);
      setPosBudget(budget);

      const syms = watchlist.filter((sym) => (sizes[sym] || 0) > 0);
      if (syms.length === 0) { setPosPrices(null); return; }

      try {
        const path = `/api/snapshot?symbols=${encodeURIComponent(syms.join(","))}`;
        const res = await backendFetch(path, path);
        if (!res.ok) throw new Error(String(res.status));
        const data = await res.json();
        if (cancelled) return;
        const out = {};
        Object.entries(data || {}).forEach(([sym, snap]) => {
          const p = snap?.latestTrade?.p;
          if (typeof p === "number" && Number.isFinite(p) && p > 0) out[sym.toUpperCase()] = p;
        });
        setPosPrices(out);
      } catch {
        if (!cancelled) setPosPrices({});   // {} = fetched and empty, null = not fetched
      }
    };

    load();
    const id = setInterval(load, REFRESH_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [watchlist]);

  // ── Dark pool — auto-populate from the platform beat cache ──
  // ⚠⚠ NO `symbols=` PARAMETER, AND THAT IS THE WHOLE FIX. /api/darkpool/scan has
  // two branches: passing symbols= runs an ad-hoc LIVE REST fan-out (one
  // _scan_symbol call per name), while omitting it serves darkpool:market:scan —
  // the cache the */30 worker beat writes 24/7, already tiered and filtered.
  // The card used to send a 12-name universe behind a SCAN NOW button, so it paid
  // for a live fan-out on demand while a fresh 218-symbol platform scan sat unread.
  // ⚠ It also read globalSignals.darkPoolScan, which has had NO PUBLISHER since the
  // dark-pool tape rebuild deleted AltDataTab's scanner — the card's primary source
  // was a dead bus key, which is why it showed "NO SCAN" on every page load.
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const path = "/api/darkpool/scan";
        const res = await backendFetch(path, path);
        if (!res.ok) throw new Error(String(res.status));
        const data = await res.json();
        if (cancelled) return;
        // ⚠ The ALPACA_API_KEY guard sits above BOTH branches server-side, so an
        // unset key returns sip_required even though the cached path needs no
        // credential. Surfaced as its own state rather than as an error.
        // ⚠ dpScan is an ARRAY once loaded, even when empty — null means "not
        // fetched yet" and nothing else. The convergence effect below keys off
        // that distinction to avoid publishing before the data has resolved.
        if (data && data.sip_required) { setDpScan([]); setDpStatus("sip"); return; }
        const results = Array.isArray(data?.results) ? data.results : [];
        setDpScan(results);
        setDpStatus(results.length ? "done" : "empty");
      } catch {
        if (!cancelled) { setDpScan([]); setDpStatus("error"); }
      }
    };

    load();
    const id = setInterval(load, REFRESH_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // ── Dark pool ∩ squeeze convergence ──
  // ⚠⚠ THIS PUBLISH IS LOAD-BEARING OUTSIDE THIS FILE. SignalBoard's
  // ruleDarkPoolSqueeze has exactly one producer — this call — so it used to fire
  // only if the user pressed SCAN NOW on this board. Keyed on both inputs because
  // the two fetches race: the dark-pool scan regularly resolves before the squeeze
  // candidate map, and computing the cross-reference inside either fetch would
  // silently drop every hit on whichever lost.
  // ⚠ Both inputs must have RESOLVED before publishing. dpScan starts null and
  // sqCandMap starts null; publishing an empty array while either is still in
  // flight would push a "no convergence" answer onto the bus before the question
  // could be asked. `onData` is safe in the deps — App memoises it with
  // useCallback([]), so this cannot self-retrigger through globalSignals.
  useEffect(() => {
    if (dpScan == null || sqCandMap == null) return;
    const hits = dpScan
      .filter((r) => r && sqCandMap[String(r.symbol).toUpperCase()])
      .map((r) => {
        const sq = sqCandMap[String(r.symbol).toUpperCase()];
        // ⚠ No dark_pct: the platform branch does not emit it (verified against
        // the live payload — the tiered shape carries total_notional and
        // print_count instead). tier_score is the platform equivalent of the
        // ad-hoc branch's `score`.
        return {
          symbol: r.symbol,
          score: num(r.tier_score),
          total_notional: num(r.total_notional),
          si_float: sq.si_float,
          dtc: sq.dtc,
          signal: "DARK POOL + SHORT SQUEEZE",
        };
      });
    setDpConvergence(hits);
    if (onData) onData("darkPoolSqueeze", hits);
  }, [dpScan, sqCandMap, onData]);

  // ── THE SYNTHESIS CONTEXT OBJECT ─────────────────────────────────────────
  // ⚠⚠ EVERY DOMAIN IS PRESENT AS A KEY, AND AN ABSENT ONE IS EXPLICITLY null.
  // The backend names null domains in the prompt ("with physical-world data
  // unavailable this session") rather than skipping them, so omitting a key here
  // would be the one thing that reintroduces the original defect: a synthesis
  // that cannot tell the difference between "nothing happened" and "I never
  // looked". Nothing in this memo fetches; it only shapes what is already loaded.
  const synthesisContext = useMemo(() => {
    const arr = (v) => (Array.isArray(v) && v.length ? v.slice(0, SYNTH_TOP_N) : null);
    const obj = (v) => (v && typeof v === "object" && !Array.isArray(v) ? v : null);

    // ⚠ Full payloads, not summaries. The component breakdowns carry the value
    // and the z-score for each input, and the z-scores are what let the model
    // separate "NEUTRAL because everything is calm" from "NEUTRAL because two
    // extremes cancelled". Sending the label alone destroys that distinction.
    const sentiment = {
      qs_fear_greed: obj(qsfg),
      bull_bear: obj(bullBear),
      stress: obj(stress),
      // ⚠⚠ STATED, NEVER INFERRED. Bull/Bear runs OPPOSITE to Fear/Greed and a
      // model shown both without this reads a crowded-long warning as bullish
      // confirmation. The backend repeats it in the domain and synthesis prompts;
      // it is carried here as well so the payload is self-describing.
      polarity_note:
        "QS Bull/Bear is a CONTRARIAN indicator and is INVERTED relative to QS " +
        "Fear/Greed. A high score (8-10) means CROWDED LONG and is a BEARISH " +
        "warning. A low score (0-2) means CAPITULATION and is a BULLISH signal. " +
        "Do not read it the same direction as Fear/Greed.",
    };

    // ⚠ Same filter as the ACTIVE SIGNALS card. A signal this board hides must
    // not reach the model either; the unfiltered list sent the hidden tanker
    // signal into the synthesis.
    const conv = Array.isArray(globalSignals?.activeSignals)
      ? globalSignals.activeSignals.filter((sg) => !_HIDDEN_SIGNAL_IDS.has(sg?.id)) : null;

    return {
      generated_at: new Date().toISOString(),
      sentiment,
      rates: obj(rates),
      physical: obj(physical),
      alt_data: obj(altdata),
      flow: {
        convergence_signals: arr(dpConvergence),
        dark_pool: arr(dpScan),
        unusual_options: arr(uoRecent),
        put_call_spy: Array.isArray(pcHist) && pcHist.length
          ? pcHist[pcHist.length - 1] : null,
      },
      aircraft_events: arr(aircraftEvents),
      active_signals: arr(conv),
      market_digest: (digest && typeof digest.digest === "string") ? digest.digest : null,
    };
  }, [qsfg, bullBear, stress, rates, physical, altdata, dpConvergence, dpScan,
      uoRecent, pcHist, aircraftEvents, digest, globalSignals]);

  // Navigate to the Dark Pool tab, handing off the local scan results so the tab
  // shows them without re-scanning; falls back to a plain navigate when there are
  // no local results to pass.
  const goDarkPool = () => {
    if (Array.isArray(dpScan) && dpScan.length && typeof onDpNavigate === "function") onDpNavigate(dpScan);
    else if (typeof onNavigate === "function") onNavigate("DARK POOL");
  };

  const loadSqueezeCands = async () => {
    setSqStatus("loading");
    try {
      const path = "/api/squeeze/candidates?limit=25&min_score=40";
      const res = await backendFetch(path, path);
      if (!res.ok) throw new Error(String(res.status));
      const data = await res.json();
      if (data && data.computing) { setSqStatus("computing"); return; }
      const cands = Array.isArray(data?.candidates) ? data.candidates : [];
      if (cands.length) { setSqCands(cands.slice(0, 2)); setSqStatus("done"); }
      else { setSqStatus("empty"); }
    } catch {
      setSqStatus("error");
    }
  };

  return (
    <div>
      {/* ─── SECTION 1 — REGIME SUMMARY ─── */}
      <div style={s.panel}>
        <div style={s.panelH}>
          <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.15em", color: C.amber }}>
            SIGNAL CONVERGENCE BOARD
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 9, color: C.textDim }}>
            <span style={{
              width: 6, height: 6, borderRadius: "50%", background: C.green,
              animation: "qsPulse 1.5s ease-in-out infinite",
            }} />
            LIVE · {updatedLabel}
          </span>
        </div>

        <div style={s.panelB}>
          {/* MARKET REGIME */}
          <div style={{ fontSize: 9, color: C.textDim, letterSpacing: "0.1em", marginBottom: 4 }}>
            MARKET REGIME
          </div>
          {loading ? (
            <div style={{ fontSize: 11, color: C.textDim, fontStyle: "italic" }}>
              Loading market regime…
            </div>
          ) : digestShown ? (
            <>
              {/* max-height carries the growth transition; both ceilings are fixed
                  (content height is unknown without measuring), so a digest shorter
                  than the cap finishes easing before it. ⚠ Both are sized to clear
                  their content at the narrowest viewport — see the derivation at
                  DIGEST_COLLAPSED_H. Neither may bind before the slice does. */}
              <div style={{
                fontSize: 12, color: C.textMid, lineHeight: 1.7,
                maxHeight: regimeOpen ? DIGEST_EXPANDED_H : DIGEST_COLLAPSED_H,
                opacity: regimeOpen ? 1 : 0.94,
                overflow: "hidden",
                transition: "max-height 260ms ease-in-out, opacity 200ms ease-in-out",
              }}>
                {digestShown}
              </div>
              {digestTruncated && (
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
                  <button
                    onClick={() => setRegimeOpen((o) => !o)}
                    style={{
                      background: "none", border: "none", cursor: "pointer",
                      color: C.textDim, fontFamily: "inherit", fontSize: 9,
                      letterSpacing: "0.1em", padding: "2px 0",
                    }}
                  >
                    {regimeOpen ? "⌃ COLLAPSE" : "⌄ READ MORE"}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div style={{ fontSize: 11, color: C.textDim, fontStyle: "italic" }}>
              Market digest generating…
            </div>
          )}

          {/* Divider */}
          <div style={{ borderTop: `1px solid ${C.border}`, margin: "12px 0" }} />

          {/* YOUR WATCHLIST */}
          <div style={{ fontSize: 9, color: C.textDim, letterSpacing: "0.1em", marginBottom: 4 }}>
            YOUR WATCHLIST
          </div>
          {/* ⚠ `flush` — the divider directly above this header is drawn by the
              parent, so the component's own top rule would land as a second line
              under a heading with nothing between the two. */}
          {/* ⚠⚠ THE `pip` BRANCH IS NOT REDUNDANT WITH THE COMPONENT'S OWN NULL
              RETURN. Rendering it unguarded would leave the YOUR WATCHLIST header
              sitting over empty space whenever the panel has not published —
              a heading with nothing under it reads as broken, not as empty. */}
          {pip ? (
            <IndependentPositions independent={pip} flush />
          ) : (
            <div style={{ fontSize: 11, color: C.textDim, lineHeight: 1.6 }}>
              Add watchlist symbols to see how the current regime maps to your basket.
            </div>
          )}
        </div>
      </div>

      {/* ─── CHYRON + SQUAWK BOX ─── */}
      {/* Self-contained: no props, reads its own settings from localStorage and
          fetches its own headlines. Nothing here threads through globalSignals. */}
      <Chyron />

      {/* ─── SECTION 2 — SENTIMENT COMPACT (QS Fear/Greed) ─── */}
      <div style={s.panel}>
        <div style={s.panelB}>
          <QSFearGreedCompact qsfg={qsfg} status={qsStatus} />
        </div>
      </div>

      {/* Prices and just-detected events. Hides itself when there is nothing to show. */}
      <LiveTape onOpen={onTapeOpen} />

      {/* ─── SECTION 3 — DOMAIN CARDS GRID ─── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 10 }}>

        {/* Card 1 · ACTIVE SIGNALS */}
        {(() => {
          // ⚠ AIS-derived signals are filtered out HERE ONLY (2026-08-15). No AIS
          // source is wired, so "N VESSELS AIS DARK" is generated from stale or
          // absent vessel data and reads as a live geopolitical alert. The title
          // string itself lives in SignalBoard.jsx (ruleTankerDarkCluster), which is
          // outside this file — so this filter suppresses the card entry on THIS
          // board only; the Signal Board tab still renders it, and the signal still
          // counts wherever else activeSignals is read.
          // TO RESTORE when an AIS source is wired: delete _HIDDEN_SIGNAL_IDS and
          // the .filter() below.
          const activeAll = Array.isArray(globalSignals?.activeSignals) ? globalSignals.activeSignals : [];
          const active = activeAll.filter((sg) => !_HIDDEN_SIGNAL_IDS.has(sg?.id));
          // ⚠ Read on every render rather than cached in state: the signal objects
          // on the bus carry NO timestamp (SignalBoard passes detectedAt to its own
          // cards as a prop and never attaches it), so the map is the only source,
          // and a mount-once read would freeze the relative ages at whatever they
          // were when this board first rendered. Cheap — one small JSON parse.
          const firstSeen = loadFirstSeen();
          const conv = Array.isArray(dpConvergence) ? dpConvergence : [];
          const firing = active.length > 0;
          const independent = new Set(active.map((sg) => sg?.type || sg?.id).filter(Boolean)).size;
          const ir = independent > 0 ? Math.sqrt(independent) : 0;
          return (
            <DomainCard
              title="ACTIVE SIGNALS"
              badge={(firing || conv.length) ? { text: String(active.length + conv.length), color: C.red } : { text: "MONITORING", color: C.textDim }}
            >
              {conv.length > 0 && (
                <div style={{ marginBottom: 4 }}>
                  {conv.slice(0, 3).map((h, i) => (
                    <div key={`cv-${h.symbol || i}`} style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 10 }}>
                      <span style={{ color: "#9D4EDD", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        ⚡ {h.symbol} · DP+SQUEEZE
                      </span>
                      {/* ⚠ Additive: inserted between the existing two spans, which keep
                          their own styling and content. Renders null when this ticker has
                          no fresh HIGH/CRITICAL alert, so the row is byte-identical to
                          before on every ticker without one. */}
                      <BarrierBadge severity={barrierAlerts[String(h.symbol || "").toUpperCase()]} />
                      <span style={{ color: C.textDim, flexShrink: 0 }}>{h.si_float != null ? `${(h.si_float * 100).toFixed(0)}% SI` : ""}</span>
                    </div>
                  ))}
                </div>
              )}
              {firing ? (
                <>
                  {active.slice(0, 3).map((sg, i) => {
                    // Personal relevance badge — only when portfolio exposures exist.
                    let rel = null;
                    if (hasPortfolio && sg) {
                      const score = computeRelevanceScore(sg, exposures);
                      const band = relevanceBand(score);
                      rel = {
                        band,
                        color: bandTextColor(band),
                        sentence: getRelevanceImplication(sg, exposures, score),
                      };
                    }
                    return (
                      <div key={sg?.id || i}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 10 }}>
                          <span style={{ color: sg?.color || C.textMid, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {sg?.title || "—"}
                          </span>
                          <span style={{ color: C.textDim, flexShrink: 0 }}>{sg?.strength || ""}</span>
                        </div>
                        {(() => {
                          // Omitted entirely when the id was never stamped — an
                          // empty "Detected —" would imply a missing reading rather
                          // than a signal this browser has not seen activate.
                          const det = detectedLabel(firstSeen, sg?.id);
                          return det ? (
                            <div style={{ fontSize: 9, color: C.textDim, marginLeft: 8, marginBottom: 2 }}>
                              {det}
                            </div>
                          ) : null;
                        })()}
                        {rel && (
                          <div style={{ fontSize: 9, lineHeight: 1.5, marginLeft: 8, marginBottom: 2 }}>
                            <span style={{ color: rel.color, fontWeight: 700, letterSpacing: "0.05em" }}>{rel.band}</span>
                            <span style={{ color: C.textMid }}> — {rel.sentence}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div style={{ fontSize: 9, color: C.textDim, marginTop: 4 }}>
                    {independent} independent position{independent === 1 ? "" : "s"} · IR ×{ir.toFixed(2)}
                  </div>
                </>
              ) : conv.length > 0 ? null : (
                <div style={{ fontSize: 10, color: C.textDim, lineHeight: 1.6 }}>
                  Board is monitoring continuously — no signal thresholds breached across the connected data sources.
                </div>
              )}
            </DomainCard>
          );
        })()}

        {/* Card 2 · RATES */}
        {(() => {
          const r = rates;
          const regime = r?.regime;
          const regimeColor = { BENIGN: C.green, ELEVATED: C.amber, STRESSED: C.red, UNKNOWN: C.textDim }[regime] || C.textDim;
          const bpsTxt = (v) => (num(v) == null ? "—" : `${num(v)} bps`);
          const pctTxt = (v) => (num(v) == null ? "—" : `${num(v).toFixed(2)}%`);
          return (
            <DomainCard title="RATES" badge={{ text: regime || "—", color: regimeColor }}
              relevance={relevanceFor("rates")} navTab="BONDS" onNavigate={onNavigate}>
              <DataLine label="Fed Funds" value={pctTxt(r?.fed_funds)} valueColor={num(r?.fed_funds) == null ? C.textDim : undefined} />
              <DataLine label="Yield 10Y-2Y" value={bpsTxt(r?.yield_spread)} valueColor={num(r?.yield_spread) == null ? C.textDim : undefined} />
              <DataLine label="HY Spread" value={bpsTxt(r?.hy_spread)} valueColor={num(r?.hy_spread) == null ? C.textDim : undefined} />
            </DomainCard>
          );
        })()}

        {/* Card 3 · FLOW */}
        {/* ⚠⚠ GEX WAS REMOVED, AND IT WAS NEVER A VALUE. The line rendered the
            literal string "Open Options → GEX" with a note that it "updates when
            Options tab is visited" — it never did. GEX is computed browser-side by
            OptionsTab's computeGex/findMagnet, neither of which is exported, and
            there is NO backend GEX endpoint, so nothing could ever have populated
            it here. It is also ticker-specific, which makes it wrong for a
            market-wide card regardless of where the number came from.
            ⚠ The dark-pool and SPY-momentum lines were dropped as duplicates: QS
            `breadth` IS the dark-pool buyer ratio and `spy_momentum` is the SPY bar,
            both already rendered in the sentiment card directly above this grid. */}
        {(() => {
          // Put/call — newest row of the DB-backed history (ascending, so last).
          const pcRows = Array.isArray(pcHist) ? pcHist : null;
          const pcLast = pcRows && pcRows.length ? pcRows[pcRows.length - 1] : null;
          const pcVal = pcLast ? num(pcLast.pc_volume_ratio) : null;
          // ⚠ Bands are put/call convention: a HIGH ratio is put-heavy = fear.
          const pcLabel = pcVal == null ? null
            : pcVal > 1.2 ? { text: "FEAR", color: C.red }
            : pcVal < 0.8 ? { text: "GREED", color: C.green }
            : { text: "NEUTRAL", color: C.amber };

          // HY spread — QS component first, dashboard/rates as the fallback.
          // ⚠⚠ THE QS COMPONENT IS USUALLY NULL, WHICH IS WHY THE FALLBACK EXISTS.
          // qs_sentiment._comp_hy needs >=200 observations and reads
          // fred:BAMLH0A0HYM2:252:desc; NOTHING warms that key on a schedule (the
          // market worker's DAILY_FRED_SERIES omits BAMLH0A0HYM2 entirely), so it
          // resolves only if a browsing session happens to have populated it.
          // Measured while building this: components.hy_spread was null while the
          // RATES card two cards to the left showed 271 bps — the SAME series, read
          // from fred_multi:* via /api/dashboard/rates, which this board already
          // fetches. Without the fallback this line would render "—" beside a
          // populated one and read as a data failure.
          // ⚠ UNITS DIFFER: the QS component carries PERCENT (2.71), the rates
          // summary carries BASIS POINTS (271). The /100 is the conversion, not a
          // cosmetic scale.
          const hy = (qsfg && qsfg.components && qsfg.components.hy_spread) || null;
          const hyQs = hy ? num(hy.value) : null;
          const hyBps = num(rates?.hy_spread);
          const hyVal = hyQs != null ? hyQs : (hyBps != null ? hyBps / 100 : null);
          const hyLabel = hyQs != null ? (hy?.label || null) : null;

          const badge = pcLabel || { text: "MONITORING", color: C.textDim };
          return (
            <DomainCard title="FLOW" badge={badge}
              relevance={relevanceFor("flow")} navTab="OPTIONS" onNavigate={onNavigate}>
              <DataLine
                label="Put/Call (SPY)"
                value={pcVal != null ? `${pcVal.toFixed(2)} · ${pcLabel.text}` : (pcRows ? "No history" : "Loading…")}
                valueColor={pcVal != null ? pcLabel.color : C.textDim}
              />
              <DataLine
                label="HY spread"
                value={hyVal != null ? `${hyVal.toFixed(2)}%${hyLabel ? ` · ${hyLabel}` : ""}` : (qsStatus === "loading" ? "Loading…" : "—")}
                valueColor={hyVal != null ? undefined : C.textDim}
              />
              {pcVal != null && (
                <div style={{ fontSize: 9, color: C.textDim, marginTop: 6, lineHeight: 1.5, fontStyle: "italic" }}>
                  {/* ⚠ Not a caveat about staleness — a statement of construction.
                      options_pc_history is written by the 16:30 ET snapshot beat,
                      so the current session never has a row. */}
                  Close-of-session reading{pcLast?.date ? ` · ${pcLast.date}` : ""}
                </div>
              )}
            </DomainCard>
          );
        })()}

        {/* Card 4 · PHYSICAL */}
        {(() => {
          const p = physical;
          const regime = p?.regime;
          // ⚠⚠ "QUIET" IS A DERIVED DEFAULT, NOT A READING. /api/dashboard/physical
          // computes the regime from altdata:tankers + altdata:thermal, and when
          // BOTH are absent — which is their normal state, since neither has a beat
          // and _serve_live only caches live data — it still returns "QUIET". The
          // badge therefore read "calm seas" when it meant "no data reached us".
          // Measured live: dark_events null, thermal_anomalies null, regime QUIET.
          const noPhysical = num(p?.dark_events) == null && num(p?.thermal_anomalies) == null;
          const badge = noPhysical ? { text: "LOADING", color: C.textDim }
            : regime === "ACTIVE" ? { text: "ACTIVE", color: C.red }
            : regime === "QUIET" ? { text: "QUIET", color: C.green }
            : { text: "—", color: C.textDim };
          const pending = (v) => (num(v) == null ? "Data loading…" : String(num(v)));
          return (
            <DomainCard title="PHYSICAL" badge={badge}
              relevance={relevanceFor("physical")} navTab="ALT DATA" onNavigate={onNavigate}>
              {/* ⚠ HIDDEN, NOT REMOVED (2026-08-15) — no AIS source is currently
                  wired, so dark_events is permanently null and the line only ever
                  said "Data loading…". TO RESTORE: uncomment the DataLine below.
                  Nothing else needs changing — the `noPhysical` guard above still
                  reads dark_events, so the badge stays correct while it is hidden
                  and starts working again the moment a source populates it.
              <DataLine label="Dark AIS events" value={pending(p?.dark_events)} valueColor={num(p?.dark_events) == null ? C.textDim : undefined} />
              */}
              <DataLine label="Thermal anomalies" value={pending(p?.thermal_anomalies)} valueColor={num(p?.thermal_anomalies) == null ? C.textDim : undefined} />
              <DataLine
                label="Regime"
                value={noPhysical ? "Data loading…" : (regime || "Data loading…")}
                valueColor={noPhysical || !regime ? C.textDim : undefined}
              />
            </DomainCard>
          );
        })()}

        {/* Card 5 · DARK POOL */}
        {(() => {
          // Derived from the platform beat cache — see the fetch effect above.
          // ⚠ `tier_score`, not `score`: the platform branch emits the tiered
          // shape (tier / tier_score / net_bias / total_notional), which is a
          // different field set from the ad-hoc live branch this card used to call.
          const results = Array.isArray(dpScan) && dpScan.length ? dpScan : null;
          const top = results ? results[0] : null;
          const topScore = top ? num(top.tier_score) : null;
          const extremeCount = results
            ? results.filter((r) => r && r.tier === "INSTITUTIONAL_ACCUMULATION").length
            : null;
          const has = !!top;
          const badge = has
            ? (topScore != null
                ? { text: `SCORE ${Math.round(topScore)}`, color: topScore >= 80 ? C.red : C.amber }
                : { text: "ACTIVE", color: C.amber })
            : dpStatus === "loading"
              ? { text: "LOADING", color: C.textDim }
              : { text: "NO DATA", color: C.textDim };
          return (
            <DomainCard title="DARK POOL" badge={badge}
              relevance={relevanceFor("darkpool")} onNavigate={onNavigate}>
              {dpConvergence.length > 0 && (
                <div style={{ marginBottom: 4 }}>
                  {dpConvergence.slice(0, 2).map((h, i) => (
                    <div key={`dpc-${h.symbol || i}`}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "#9D4EDD", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        ⚡ CONVERGENCE: {h.symbol}
                      </div>
                      <div style={{ fontSize: 9, color: C.textMid, marginLeft: 8 }}>
                        Dark pool + {h.si_float != null ? (h.si_float * 100).toFixed(1) : "—"}% short interest
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {has ? (
                <>
                  <DataLine label="Top" value={`${top.symbol || "—"}${top.net_bias ? ` · ${top.net_bias}` : ""}`} />
                  <DataLine label="Score" value={topScore != null ? Math.round(topScore) : "—"} />
                  <DataLine label="Notional" value={fmtNotional(top.total_notional)} />
                  <DataLine label="Accumulation" value={extremeCount != null ? extremeCount : "—"} />
                  <CardNavLink label="→ Dark Pool Scanner" tab="DARK POOL" onNavigate={onNavigate} onClick={goDarkPool} />
                </>
              ) : dpStatus === "sip" ? (
                <div style={{ fontSize: 10, color: C.textDim, lineHeight: 1.6 }}>
                  Full-market data feed required
                </div>
              ) : dpStatus === "loading" ? (
                <div style={{ fontSize: 10, color: C.textDim, lineHeight: 1.6 }}>
                  Loading platform scan…
                </div>
              ) : dpStatus === "error" ? (
                <div style={{ fontSize: 10, color: C.red, lineHeight: 1.6 }}>
                  Scan unavailable — retrying on the next refresh
                </div>
              ) : (
                <div style={{ fontSize: 10, color: C.textDim, lineHeight: 1.6 }}>
                  No off-exchange prints above threshold
                </div>
              )}
            </DomainCard>
          );
        })()}

        {/* Card 6 · SQUEEZE */}
        {(() => {
          const sq = globalSignals?.squeezeConvergence;
          const score = num(sq?.score);
          const has = !!(sq && sq.symbol);
          const local = Array.isArray(sqCands) && sqCands.length ? sqCands : null;
          const localTop = local ? num(local[0]?.total_score) : null;
          const badge = has
            ? (score != null ? { text: `SCORE ${Math.round(score)}`, color: score >= 80 ? C.red : C.amber } : { text: "CONVERGENCE", color: C.amber })
            : local
              ? (localTop != null ? { text: `SCORE ${Math.round(localTop)}`, color: localTop >= 80 ? C.red : C.amber } : { text: "CANDIDATES", color: C.amber })
              : { text: "NO SCAN", color: C.textDim };
          return (
            <DomainCard title="SQUEEZE" badge={badge}
              relevance={relevanceFor("squeeze")} onNavigate={onNavigate}>
              {has ? (
                <>
                  <DataLine label="Convergence" value={sq.symbol} valueColor={C.amber} />
                  <DataLine label="Score" value={score != null ? Math.round(score) : "—"} />
                  <DataLine label="DP bias" value={sq.dp_bias || "—"} />
                  <CardNavLink label="→ Squeeze Scanner" tab="SQUEEZE" onNavigate={onNavigate} />
                </>
              ) : local ? (
                <>
                  {local.map((c, i) => (
                    <div key={c.symbol || i}>
                      <div style={{ fontSize: 10, color: C.textMid, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {c.symbol || "—"} Score {num(c.total_score) != null ? Math.round(c.total_score) : "—"} — {c.signal || "—"}
                      </div>
                      <div style={{ fontSize: 9, color: C.textDim, marginLeft: 8 }}>
                        SI: {num(c.si_float) != null ? (c.si_float * 100).toFixed(1) : "—"}% · DTC: {num(c.dtc) != null ? c.dtc : "—"}d
                      </div>
                    </div>
                  ))}
                  <CardNavLink label="→ Squeeze Scanner" tab="SQUEEZE" onNavigate={onNavigate} />
                </>
              ) : sqStatus === "computing" ? (
                <>
                  <div style={{ fontSize: 10, color: C.textDim, lineHeight: 1.6, marginBottom: 4 }}>
                    Universe scoring in progress
                  </div>
                  <TriggerButton label="LOAD CANDIDATES" onClick={loadSqueezeCands} disabled={false} />
                </>
              ) : (
                <>
                  <div style={{ fontSize: 10, color: C.textDim, lineHeight: 1.6, marginBottom: 4 }}>
                    Short-squeeze candidates from the nightly pre-score.
                  </div>
                  <TriggerButton
                    label={sqStatus === "loading" ? "LOADING..." : "LOAD CANDIDATES"}
                    onClick={loadSqueezeCands}
                    disabled={sqStatus === "loading"}
                  />
                  {sqStatus === "error" && (
                    <div style={{ fontSize: 9, color: C.red, textAlign: "center", marginTop: 4 }}>Load failed — try again</div>
                  )}
                  {sqStatus === "empty" && (
                    <div style={{ fontSize: 9, color: C.textDim, textAlign: "center", marginTop: 4 }}>No candidates above threshold</div>
                  )}
                </>
              )}
            </DomainCard>
          );
        })()}

        {/* Card 7 · ALT DATA */}
        {(() => {
          const a = altdata;
          const regime = a?.regime;
          const badge = regime === "FLOWING" ? { text: "FLOWING", color: C.green }
            : regime === "QUIET" ? { text: "QUIET", color: C.textDim }
            : { text: "—", color: C.textDim };
          // ⚠⚠ THREE DIFFERENT CONGRESSIONAL QUANTITIES — NEVER INTERCHANGEABLE:
          //   composition  — real SEAT counts, from an actual roster
          //                  (congress:composition, congress.gov / congress-legislators)
          //   party_split  — DISTINCT MEMBERS who filed trades
          //   trade_count  — disclosure LINE-ITEMS filed
          // This card used to render party_split as "{D}D / {R}R" back when it was a
          // ROW count, which read as the makeup of Congress — it was one prolific
          // filer (703 of 1,026 rows came from a single PTR). The labels below say
          // which is which; do not shorten them back to a bare "Party split".
          const ps = a?.party_split;
          const comp = a?.composition;
          const seats = (ch) => {
            if (!ch || typeof ch !== "object") return null;
            const R = num(ch.R), D = num(ch.D), I = num(ch.I);
            if (R == null && D == null) return null;
            return `${R ?? 0}R / ${D ?? 0}D${I ? ` / ${I}I` : ""}`;
          };
          const senate = seats(comp?.senate);
          const house = seats(comp?.house);
          const members = ps && (num(ps.R) != null || num(ps.D) != null)
            ? `${num(ps.R) ?? 0}R / ${num(ps.D) ?? 0}D`
            : null;
          const unresolved = num(ps?.unknown);
          // trade_count is the current field; congress_trades is its retained alias,
          // so an older backend (or a stale dashboard:altdata cache) still renders.
          const filings = num(a?.trade_count) ?? num(a?.congress_trades);
          const cv = num(a?.contract_value);
          return (
            <DomainCard title="ALT DATA" badge={badge}
              relevance={relevanceFor("altdata")} navTab="ALT DATA" onNavigate={onNavigate}>
              {/* Seats are OMITTED, not dashed, when congress:composition is cold —
                  mirroring the backend rule that seat counts are never approximated
                  from filings. */}
              {senate && <DataLine label="Senate seats" value={senate} />}
              {house && <DataLine label="House seats" value={house} />}
              {(senate || house) && (
                <div style={{ borderTop: `1px solid ${C.border}`, margin: "2px 0" }} />
              )}
              <DataLine label="Members trading" value={members || "—"} valueColor={members ? undefined : C.textDim} />
              {unresolved ? (
                <DataLine label="Party unresolved" value={String(unresolved)} valueColor={C.textDim} />
              ) : null}
              <DataLine label="Filings" value={filings != null ? filings.toLocaleString() : "—"} valueColor={filings == null ? C.textDim : undefined} />
              <DataLine label="Contract value" value={cv != null ? "$" + cv.toLocaleString(undefined, { maximumFractionDigits: 0 }) : "—"} valueColor={cv == null ? C.textDim : undefined} />
            </DomainCard>
          );
        })()}

      </div>

      {/* ─── SECTION 4 — BOTTOM ROW (full synthesis) ─── */}
      {/* ⚠ The Watchlist Context card that used to share this row moved into the
          Section 3 grid in 2026-08-15 and was REMOVED entirely when the
          independent-position read took over the YOUR WATCHLIST slot in Section 1
          — so this row holds one card for a second reason now. The grid is LEFT AS
          auto-fit deliberately:
          with one child, `repeat(auto-fit, minmax(240px, 1fr))` already resolves to
          a single full-width column, so no style change was needed and the
          two-column behaviour returns by itself if a second card is ever added
          back. It still stacks correctly below ~490px. */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 10, marginTop: 10 }}>

        {/* FULL SYNTHESIS (tier-gated) */}
        {(() => {
          let tier = "";
          try { tier = localStorage.getItem("qs_tier") || ""; } catch { tier = ""; }
          const isNT = tier === "nous_topology";
          const calls = num(globalSignals?.callsRemaining); // omit when absent

          if (isNT) {
            // Bubble Tray context — append compact portfolio context only when BOTH
            // portfolioExposures and portfolioIndependentPositions are present.
            let btContext = "Cross-asset signal convergence board synthesis";
            const exps = globalSignals?.portfolioExposures;
            const pipc = globalSignals?.portfolioIndependentPositions;
            if (exps && pipc) {
              const fv = (x) => (typeof x === "number" && Number.isFinite(x) ? x.toFixed(2) : "—");
              const grp = Array.isArray(pipc.groups) && pipc.groups.length ? pipc.groups[0] : null;
              const wt = grp && typeof grp.combinedWeight === "number" && Number.isFinite(grp.combinedWeight)
                ? Math.round(grp.combinedWeight * 100) : null;
              const grpName = grp && grp.name ? grp.name : "—";
              const count = typeof pipc.independentPositions === "number" && Number.isFinite(pipc.independentPositions)
                ? pipc.independentPositions : "—";
              btContext +=
                "\n\nPORTFOLIO CONTEXT:\n" +
                `Beta ${fv(exps.beta)} | Momentum ${fv(exps.momentum)} | ` +
                `Value ${fv(exps.value)} | Quality ${fv(exps.quality)} | ` +
                `${count} independent positions | ` +
                `Largest group: ${wt != null ? wt + "%" : "—"} ${grpName}`;
            }
            return (
              <div style={{ ...s.card(C.purple), marginBottom: 0, display: "flex", flexDirection: "column" }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", color: C.purple, marginBottom: 6 }}>
                  FULL SYNTHESIS
                </div>
                <div style={{ flex: 1, fontSize: 11, color: C.textMid, lineHeight: 1.6 }}>
                  One read that ties rates, flow, physical-world, sentiment, and alt-data signals into a single cross-asset picture.
                </div>
                {calls != null && (
                  <div style={{ fontSize: 9, color: C.textDim, marginTop: 6 }}>
                    {calls} full {calls === 1 ? "analysis" : "analyses"} left today
                  </div>
                )}
                <div style={{ marginTop: 8 }}>
                  {/* ⚠ The synthesis context now travels on its own prop. It used to
                      be spread into `globalSignals` because AnalystButton had no
                      channel for it — which put a panel's request payload inside the
                      cross-tab data bus and spread a fresh object on every render.
                      The bus carries only what it is meant to carry again. */}
                  <AnalystButton
                    panel="signal_board"
                    label="⚡ SYNTHESIZE"
                    globalSignals={globalSignals}
                    clientContext={synthesisContext}
                    context={btContext}
                  />
                </div>
              </div>
            );
          }

          return (
            <div style={{ ...s.card(), marginBottom: 0, display: "flex", flexDirection: "column" }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", color: C.textDim, marginBottom: 6 }}>
                FULL SYNTHESIS
              </div>
              <div style={{ fontSize: 11, color: C.textDim, lineHeight: 1.6 }}>
                A single cross-asset synthesis across all five signal domains is available on a higher tier.
              </div>
            </div>
          );
        })()}

      </div>
    </div>
  );
}
